import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Gavel,
  ShieldCheck,
  Users,
  ChevronRight,
  Target,
  TrendingUp,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { audiences, images } from "../data/mock";
import { useSiteConfig } from "../contexts/SiteConfigContext";
import { usePageSEO } from "../hooks/usePageSEO";

const iconMap = {
  Building2,
  Gavel,
  ShieldCheck,
  Users,
};

export default function WhoNeedsThisPage() {
  const { audiences } = useSiteConfig();
  
  usePageSEO({
    title: "Who Needs Polygraph Services",
    description: "Discover who benefits from polygraph services - corporations, HR departments, legal professionals, and individuals seeking truth verification.",
    path: "/who-needs-this"
  });
  
  return (
    <main>
      {/* Hero Banner */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-[#0A0A0F]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#DC2626]/[0.03] to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-[#DC2626] font-semibold tracking-[0.3em] text-xs mb-4 uppercase">
            Who Needs This
          </p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Is Polygraph Testing
            <br />
            <span className="text-[#DC2626]">Right for You?</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl leading-relaxed">
            Discover how polygraph services can address your specific situation
            — whether you represent a corporation, legal practice, security
            firm, or are an individual seeking answers.
          </p>
        </div>
      </section>

      {/* Audience Sections */}
      <section className="bg-[#0A0A0F] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-20 lg:space-y-28">
            {audiences.map((audience, aIdx) => {
              const Icon = iconMap[audience.icon] || ShieldCheck;
              return (
                <div key={audience.id}>
                  {/* Audience Header */}
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-12">
                    <div className="lg:col-span-5">
                      <div className="flex items-center gap-4 mb-5">
                        <div className="w-12 h-12 rounded-xl bg-[#DC2626]/[0.08] flex items-center justify-center">
                          <Icon className="h-5 w-5 text-[#DC2626]" />
                        </div>
                        <span className="text-[#DC2626]/30 font-display text-4xl font-bold">
                          {String(aIdx + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
                        {audience.title}
                      </h2>
                      <p className="text-gray-400 leading-relaxed">
                        {audience.description}
                      </p>
                    </div>
                    <div className="lg:col-span-7">
                      {/* Scenario Cards */}
                      <div className="space-y-4">
                        {audience.scenarios.map((scenario, sIdx) => (
                          <div
                            key={sIdx}
                            className="bg-[#111118] border border-white/[0.04] rounded-2xl p-6 lg:p-8 hover:border-[#DC2626]/15 transition-all duration-500"
                          >
                            <h4 className="text-white font-semibold text-base mb-4 flex items-center gap-2">
                              <ChevronRight className="h-4 w-4 text-[#DC2626]" />
                              {scenario.title}
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <Target className="h-3.5 w-3.5 text-gray-500" />
                                  <span className="text-gray-500 text-xs font-semibold uppercase tracking-wider">
                                    Scenario
                                  </span>
                                </div>
                                <p className="text-gray-400 text-sm leading-relaxed">
                                  {scenario.example}
                                </p>
                              </div>
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <TrendingUp className="h-3.5 w-3.5 text-[#DC2626]" />
                                  <span className="text-[#DC2626] text-xs font-semibold uppercase tracking-wider">
                                    Outcome
                                  </span>
                                </div>
                                <p className="text-gray-300 text-sm leading-relaxed">
                                  {scenario.outcome}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {/* CTA for this audience */}
                      <div className="mt-6">
                        <Link to="/contact">
                          <Button className="bg-[#DC2626]/10 hover:bg-[#DC2626] text-[#DC2626] hover:text-white border border-[#DC2626]/30 hover:border-[#DC2626] font-medium px-6 transition-all duration-300">
                            Request a Quote for {audience.title}
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  {aIdx < audiences.length - 1 && (
                    <div className="border-b border-white/[0.04]" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0D0D14] py-24 lg:py-28 mt-12">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
            Don't See Your Situation Listed?
          </h2>
          <p className="text-gray-400 text-lg mb-10 leading-relaxed">
            Every case is unique. Contact us to discuss your specific needs and
            we'll create a tailored approach that delivers the answers you
            need.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-[#DC2626] hover:bg-[#B91C1C] text-white px-10 h-13 text-base font-medium transition-colors duration-200"
            >
              Contact Us Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
}
